import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ArrowLeft,
  Copy,
  Edit3,
  Gamepad2,
  Gift,
  IdCard,
  KeyRound,
  MinusCircle,
  PlusCircle,
  ReceiptText,
  Save,
  Search,
  ShieldCheck,
  Trash2,
  Users,
  WalletCards,
  X,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import useAdminAutoRefresh from "../../lib/useAdminAutoRefresh";
import { isVerifiedUser, VerifiedName } from "../../components/VerifiedBadge";
import { formatKycPendingReason } from "../../lib/kyc";

const MONEY_UNIT = 1000;

const DETAIL_TABS = [
  { key: "overview", label: "Overview" },
  { key: "wallet", label: "Wallet" },
  { key: "profile", label: "Profile" },
  { key: "game_accounts", label: "Game Accounts" },
  { key: "transactions", label: "Transactions" },
  { key: "referrals", label: "Referrals" },
];

const TYPE_LABELS = {
  admin_credit: "Admin Credit",
  admin_debit: "Admin Debit",
  inactivity_wallet_expiry: "Wallet Inactivity Expiry",
  inactivity_freeplay_expiry: "Freeplay Inactivity Expiry",
  admin_freeplay_credit: "Freeplay Credit",
  admin_freeplay_debit: "Freeplay Debit",
  admin_manual_verify: "Manual Verify",
  admin_password_reset: "Password Reset",
  admin_user_deleted: "Account Deleted",
  bonus_credit: "Bonus Credit",
  create_account: "Create Account",
  free_credit: "Free Credit",
  game_load: "Game Load",
  game_redeem: "Game Redeem",
  load: "Load",
  phone_claim_load: "Phone Claim",
  identity_verification: "ID Verification",
  redeem: "Redeem",
  referral_bonus: "Referral Bonus",
  reset_password: "Game Password Reset",
  wallet_transfer_received: "Transfer Received",
  wallet_transfer_sent: "Transfer Sent",
};

const HIDDEN_TRANSACTION_TYPES = new Set(["create_account"]);
const MEMBERS_PAGE_SIZE = 100;

function formatMoney(units = 0) {
  const dollars = Number(units || 0) / MONEY_UNIT;
  return dollars.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function dollarsToUnits(value) {
  return Math.round(Number(value) * MONEY_UNIT);
}

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString();
}

async function copyToClipboard(text, successMessage = "Copied") {
  const value = String(text || "").trim();
  if (!value) {
    toast.error("Nothing to copy");
    return;
  }
  try {
    await navigator.clipboard.writeText(value);
    toast.success(successMessage);
  } catch {
    toast.error("Could not copy");
  }
}

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    if (!file) {
      resolve("");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(reader.error || new Error("Could not read file"));
    reader.readAsDataURL(file);
  });
}

function typeLabel(type) {
  return TYPE_LABELS[type] || String(type || "-").replaceAll("_", " ");
}

function statusTone(status) {
  if (status === "approved" || status === "active" || status === "ready") return "border-emerald-500/30 bg-emerald-500/10 text-emerald-300";
  if (status === "pending") return "border-amber-500/30 bg-amber-500/10 text-amber-300";
  if (status === "rejected" || status === "banned") return "border-red-500/30 bg-red-500/10 text-red-300";
  return "border-white/10 bg-white/5 text-slate-300";
}

function isWalletTransaction(row) {
  if (!row) return false;
  if (row.payment_provider || row.payment_platform || row.payment_method_id || row.gateway_transaction_id || row.transfer_kind === "wallet_transfer") return true;
  if (["admin_credit", "admin_debit", "admin_freeplay_credit", "admin_freeplay_debit", "bonus_credit", "free_credit", "load", "redeem", "phone_claim_load", "referral_bonus", "wallet_transfer_received", "wallet_transfer_sent"].includes(row.type)) {
    return !row.game_id && !row.gameName && !row.gameId;
  }
  return false;
}

export default function AdminUsers() {
  const { user: authUser } = useAuth();
  const canManageMembers = authUser?.role === "admin";
  const [users, setUsers] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [adjusting, setAdjusting] = useState(null);
  const [adjustKind, setAdjustKind] = useState("wallet");
  const [direction, setDirection] = useState("credit");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [resetting, setResetting] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [resetNote, setResetNote] = useState("");
  const [deleting, setDeleting] = useState(null);
  const [accountModalOpen, setAccountModalOpen] = useState(false);
  const [accountEditing, setAccountEditing] = useState(null);
  const [accountForm, setAccountForm] = useState({ game_id: "", username: "", password: "", provider_user_id: "", note: "" });
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [userPage, setUserPage] = useState(1);
  const [verificationFilter, setVerificationFilter] = useState("all");
  const [userMeta, setUserMeta] = useState({
    total: 0,
    all: 0,
    verified: 0,
    unverified: 0,
    page: 1,
    page_size: MEMBERS_PAGE_SIZE,
  });

  const load = useCallback((term = "", page = 1, verification = "all") => {
    const q = String(term || "").trim();
    const params = {
      paginated: 1,
      page,
      page_size: MEMBERS_PAGE_SIZE,
      verification,
      ...(q ? { q } : {}),
    };
    return api.get("/admin/users", { params }).then(({ data }) => {
      const rows = Array.isArray(data) ? data : data.users || [];
      setUsers(rows);
      setUserMeta({
        total: Number(data.total ?? rows.length),
        all: Number(data.all ?? rows.length),
        verified: Number(data.verified ?? rows.filter((user) => isVerifiedUser(user)).length),
        unverified: Number(data.unverified ?? Math.max(0, rows.length - rows.filter((user) => isVerifiedUser(user)).length)),
        page: Number(data.page ?? page),
        page_size: Number(data.page_size ?? MEMBERS_PAGE_SIZE),
      });
    });
  }, []);

  const loadDetail = useCallback(async (userId, { quiet = false } = {}) => {
    if (!userId) return;
    if (!quiet) setDetailLoading(true);
    try {
      const { data } = await api.get(`/admin/users/${userId}`);
      setDetail(data);
    } catch (err) {
      if (!quiet) {
        toast.error(formatApiError(err));
        setSelectedId(null);
        setDetail(null);
      }
    } finally {
      if (!quiet) setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      load(search, userPage, verificationFilter).catch((err) => toast.error(formatApiError(err)));
    }, search.trim() ? 250 : 0);
    return () => window.clearTimeout(timer);
  }, [load, search, userPage, verificationFilter]);

  useAdminAutoRefresh(() => {
    load(search, userPage, verificationFilter).catch(() => {});
    if (selectedId && !adjusting && !resetting && !deleting && !accountModalOpen) {
      loadDetail(selectedId, { quiet: true }).catch(() => {});
    }
  });

  const selectedListUser = useMemo(
    () => users.find((user) => user.id === selectedId) || null,
    [selectedId, users]
  );

  const openDetail = (user) => {
    setSelectedId(user.id);
    setDetail(null);
    setActiveTab("overview");
    loadDetail(user.id);
  };

  const closeDetail = () => {
    setSelectedId(null);
    setDetail(null);
    setActiveTab("overview");
  };

  const updateSearch = (value) => {
    setUserPage(1);
    setSearch(value);
  };

  const updateVerificationFilter = (value) => {
    setUserPage(1);
    setVerificationFilter(value);
  };

  const mergeUpdatedUser = (user) => {
    if (!user) return;
    setUsers((rows) => rows.map((row) => (row.id === user.id ? user : row)));
    setDetail((current) => (
      current?.user?.id === user.id ? { ...current, user } : current
    ));
  };

  const refreshSelectedDetail = async () => {
    if (selectedId) await loadDetail(selectedId);
  };

  const openAdjust = (user, nextDirection, nextKind = "wallet") => {
    setAdjusting(user);
    setAdjustKind(nextKind);
    setDirection(nextDirection);
    setAmount("");
    setNote("");
  };

  const openReset = (user) => {
    setResetting(user);
    setNewPassword(generatePassword());
    setResetNote("");
  };

  const openDelete = (user) => {
    setDeleting(user);
  };

  const openAccountEditor = (account = null) => {
    const firstGame = detail?.games?.[0]?.value || "";
    setAccountEditing(account);
    setAccountForm({
      game_id: account?.gameName || account?.gameId || account?.game_key || firstGame,
      username: account?.username || "",
      password: account?.password || "",
      provider_user_id: account?.provider_user_id || "",
      note: "",
    });
    setAccountModalOpen(true);
  };

  const submitAdjust = async (e) => {
    e.preventDefault();
    const dollarAmount = Number(amount);
    const unitsAmount = dollarsToUnits(amount);
    if (!adjusting) return;
    if (!Number.isFinite(dollarAmount) || dollarAmount <= 0 || unitsAmount <= 0) {
      toast.error("Enter a valid dollar amount");
      return;
    }
    setSaving(true);
    try {
      const endpoint = adjustKind === "free_play" ? "/admin/free-play/adjust" : "/admin/wallet/adjust";
      const adjustmentLabel = adjustKind === "free_play" ? "free play" : "wallet money";
      const { data } = await api.post(endpoint, {
        user_id: adjusting.id,
        direction,
        amount: unitsAmount,
        note: note.trim() || `${direction === "credit" ? "Added" : "Minus"} ${formatMoney(unitsAmount)} ${adjustmentLabel} from Users page`,
      });
      mergeUpdatedUser(data.user);
      setAdjusting(null);
      await refreshSelectedDetail();
      toast.success(`${adjustKind === "free_play" ? "Free play" : "Wallet money"} ${direction === "credit" ? "added" : "removed"}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    if (!resetting) return;
    if (newPassword.trim().length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/admin/users/reset-password", {
        user_id: resetting.id,
        new_password: newPassword.trim(),
        note: resetNote.trim(),
      });
      mergeUpdatedUser(data.user);
      setResetting(null);
      await refreshSelectedDetail();
      toast.success("User password reset");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const submitManualVerify = async ({ phone, verifyPhone = false } = {}) => {
    if (!detail?.user?.id) return;
    const phoneText = typeof phone === "string" ? phone.trim() : undefined;
    if (!phoneText && !verifyPhone) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/admin/users/${detail.user.id}/manual-verify`, {
        phone: phoneText,
        phone_verified: Boolean(verifyPhone),
        note: verifyPhone ? "Manual phone verification from admin user profile" : "Manual phone update from admin user profile",
      });
      setDetail(data.detail);
      mergeUpdatedUser(data.user);
      const credited = Number(data.free_play_credited || 0);
      toast.success(credited > 0 ? `Phone verified - ${formatMoney(credited)} Freeplay added` : "Phone profile saved");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const submitIdentityReview = async ({ status, note = "" } = {}) => {
    if (!detail?.user?.id || !status) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/admin/users/${detail.user.id}/identity-review`, {
        status,
        note,
      });
      setDetail(data.detail);
      mergeUpdatedUser(data.user);
      const credited = Number(data.free_play_credited || 0);
      toast.success(credited > 0 ? `ID ${status} - ${formatMoney(credited)} Freeplay added` : `ID verification ${status}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const submitIdentityDocuments = async ({ documents, approve = false, note = "" } = {}) => {
    if (!detail?.user?.id) return;
    const files = documents || {};
    if (!files.front && !files.back && !files.selfie && !approve) {
      toast.error("Choose at least one KYC photo.");
      return;
    }
    setSaving(true);
    try {
      const [frontUrl, backUrl, selfieUrl] = await Promise.all([
        readFileAsDataUrl(files.front),
        readFileAsDataUrl(files.back),
        readFileAsDataUrl(files.selfie),
      ]);
      const payload = {
        approve,
        note,
        id_image_url: frontUrl || undefined,
        id_image_name: files.front?.name || "",
        id_back_image_url: backUrl || undefined,
        id_back_image_name: files.back?.name || "",
        selfie_image_url: selfieUrl || undefined,
        selfie_image_name: files.selfie?.name || "",
      };
      const { data } = await api.post(`/admin/users/${detail.user.id}/identity-documents`, payload);
      setDetail(data.detail);
      mergeUpdatedUser(data.user);
      const credited = Number(data.free_play_credited || 0);
      toast.success(approve
        ? credited > 0
          ? `Manual KYC approved - ${formatMoney(credited)} Freeplay added`
          : "Manual KYC approved"
        : "Manual KYC photos saved");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const refreshDiditIdentity = async () => {
    if (!detail?.user?.id) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/admin/users/${detail.user.id}/didit-refresh`);
      setDetail(data.detail);
      mergeUpdatedUser(data.user);
      toast.success(`Didit status refreshed: ${String(data.status || "pending").replaceAll("_", " ")}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const submitDelete = async (e) => {
    e.preventDefault();
    if (!deleting) return;
    setSaving(true);
    try {
      await api.delete(`/admin/users/${deleting.id}`);
      setUsers((rows) => rows.filter((u) => u.id !== deleting.id));
      await load(search, userPage, verificationFilter);
      if (selectedId === deleting.id) closeDetail();
      setDeleting(null);
      toast.success("Account deleted");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const submitAccount = async (e) => {
    e.preventDefault();
    if (!detail?.user?.id) return;
    const payload = {
      game_id: accountForm.game_id.trim(),
      username: accountForm.username.trim(),
      password: accountForm.password.trim(),
      provider_user_id: accountForm.provider_user_id.trim(),
      note: accountForm.note.trim(),
    };
    if (!payload.game_id || !payload.username || !payload.password) {
      toast.error("Game, username and password are required");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post(`/admin/users/${detail.user.id}/game-accounts`, payload);
      setDetail(data.detail);
      setAccountModalOpen(false);
      setAccountEditing(null);
      toast.success(accountEditing ? "Game account updated" : "Game account added");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const selectedUser = detail?.user || selectedListUser;

  return (
    <div className="p-3 sm:p-6 md:p-8">
      {selectedId ? (
        <UserDetailView
          activeTab={activeTab}
          canManage={canManageMembers}
          detail={detail}
          detailLoading={detailLoading}
          onAddAccount={() => openAccountEditor(null)}
          onBack={closeDetail}
          onDelete={openDelete}
          onEditAccount={openAccountEditor}
          onIdentityDocuments={submitIdentityDocuments}
          onIdentityReview={submitIdentityReview}
          onDiditRefresh={refreshDiditIdentity}
          onRefresh={() => refreshSelectedDetail()}
          onManualVerify={submitManualVerify}
          onReset={openReset}
          saving={saving}
          onTab={setActiveTab}
          onWalletAdjust={openAdjust}
          user={selectedUser}
        />
      ) : (
        <UsersTable
          canManage={canManageMembers}
          onAdjust={openAdjust}
          onDelete={openDelete}
          onOpenDetail={openDetail}
          onReset={openReset}
          onSearch={updateSearch}
          onPage={setUserPage}
          onVerificationFilter={updateVerificationFilter}
          search={search}
          meta={userMeta}
          page={userPage}
          users={users}
          verificationFilter={verificationFilter}
        />
      )}

      {adjusting && (
        <AdjustDialog
          adjustKind={adjustKind}
          adjusting={adjusting}
          amount={amount}
          direction={direction}
          note={note}
          onAmount={setAmount}
          onClose={() => setAdjusting(null)}
          onDirection={setDirection}
          onNote={setNote}
          onSubmit={submitAdjust}
          saving={saving}
        />
      )}

      {resetting && (
        <ResetPasswordDialog
          newPassword={newPassword}
          onClose={() => setResetting(null)}
          onGenerate={() => setNewPassword(generatePassword())}
          onNewPassword={setNewPassword}
          onNote={setResetNote}
          onSubmit={submitReset}
          resetNote={resetNote}
          resetting={resetting}
          saving={saving}
        />
      )}

      {deleting && (
        <DeleteDialog
          deleting={deleting}
          onClose={() => setDeleting(null)}
          onSubmit={submitDelete}
          saving={saving}
        />
      )}

      {accountModalOpen && (
        <GameAccountDialog
          accountEditing={accountEditing}
          form={accountForm}
          games={detail?.games || []}
          onChange={(patch) => setAccountForm((current) => ({ ...current, ...patch }))}
          onClose={() => {
            setAccountModalOpen(false);
            setAccountEditing(null);
          }}
          onSubmit={submitAccount}
          saving={saving}
        />
      )}
    </div>
  );
}

function UsersTable({ canManage, meta, onAdjust, onDelete, onOpenDetail, onPage, onReset, onSearch, onVerificationFilter, page, search, users, verificationFilter }) {
  const filteredUsers = users;
  const pageSize = Number(meta?.page_size || MEMBERS_PAGE_SIZE);
  const total = Number(meta?.total || users.length);
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const pageStart = total > 0 ? (page - 1) * pageSize + 1 : 0;
  const pageEnd = total > 0 ? Math.min(total, (page - 1) * pageSize + filteredUsers.length) : 0;
  const verifiedCount = Number(meta?.verified || 0);
  const unverifiedCount = Number(meta?.unverified || 0);
  const filterLabel = verificationFilter === "verified" ? "verified" : verificationFilter === "unverified" ? "unverified" : "";
  const filterButtons = [
    { key: "all", label: "All", count: Number(meta?.all || total || users.length) },
    { key: "verified", label: "Verified", count: verifiedCount },
    { key: "unverified", label: "Unverified", count: unverifiedCount },
  ];

  return (
    <>
      <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Members</h1>
          <p className="mt-1 text-sm text-slate-500">
            {search.trim() || verificationFilter !== "all"
              ? `${pageStart}-${pageEnd} of ${total} ${filterLabel ? `${filterLabel} ` : ""}accounts`
              : `${total} accounts`}
          </p>
        </div>
        <div className="flex w-full flex-col gap-2 lg:w-auto lg:items-end">
          <div className="flex w-full flex-wrap gap-2 lg:justify-end">
            {filterButtons.map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={() => onVerificationFilter(item.key)}
                className={`inline-flex h-9 items-center gap-2 rounded-full border px-3 text-xs font-bold transition ${
                  verificationFilter === item.key
                    ? "border-[#2F6FA5] bg-[#2F6FA5]/20 text-[#B7D7F2]"
                    : "border-white/10 bg-[#0d0d10] text-slate-400 hover:border-[#2F6FA5]/50 hover:text-slate-100"
                }`}
              >
                <span>{item.label}</span>
                <span className="rounded-full bg-white/10 px-1.5 py-0.5 text-[10px] text-slate-300">{item.count}</span>
              </button>
            ))}
          </div>
          <label className="relative w-full lg:w-[380px]">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
            <input
              value={search}
              onChange={(e) => onSearch(e.target.value)}
              placeholder="Search users"
              className="h-11 w-full rounded-xl border border-white/10 bg-[#0d0d10] pl-10 pr-4 text-sm text-slate-100 outline-none transition-colors placeholder:text-slate-500 focus:border-[#2F6FA5]/70"
            />
          </label>
        </div>
      </div>
      <div className="overflow-x-auto rounded-lg border border-white/10 bg-[#121214]">
        <table className="min-w-[1100px] w-full text-sm">
          <thead className="bg-white/5 text-left text-xs uppercase tracking-wider text-slate-400">
            <tr>
              <th className="px-4 py-3">Username</th>
              <th className="px-4 py-3">Email</th>
              <th className="px-4 py-3">Role</th>
              <th className="px-4 py-3">Verified</th>
              <th className="px-4 py-3 text-right">Wallet</th>
              <th className="px-4 py-3 text-right">Free play</th>
              <th className="px-4 py-3">Created</th>
              {canManage && <th className="px-4 py-3 text-right">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map((u) => (
              <tr
                key={u.id}
                data-testid={`admin-user-row-${u.id}`}
                onClick={() => onOpenDetail(u)}
                className="cursor-pointer border-t border-white/5 transition hover:bg-white/[0.035]"
              >
                <td className="px-4 py-3 font-medium text-white">
                  <VerifiedName name={u.username || "Player"} user={u} />
                </td>
                <td className="px-4 py-3 text-slate-400">{u.email}</td>
                <td className="px-4 py-3">
                  <span className={`rounded-md border px-2 py-0.5 text-xs ${
                    u.role === "admin" ? "bg-amber-900/30 border-amber-700/40 text-amber-300" : "bg-slate-800 border-white/10 text-slate-300"
                  }`}>{u.role}</span>
                </td>
                <td className="px-4 py-3">
                  {isVerifiedUser(u) ? (
                    <span className="rounded-md border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-xs font-semibold text-emerald-300">
                      Verified
                    </span>
                  ) : (
                    <span className="rounded-md border border-red-500/30 bg-red-500/10 px-2 py-0.5 text-xs font-semibold text-red-300">
                      Not verified
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-right tabular-nums">{formatMoney(u.coins)}</td>
                <td className="px-4 py-3 text-right tabular-nums text-emerald-200">{formatMoney(u.free_play || 0)}</td>
                <td className="px-4 py-3 text-xs text-slate-500">{new Date(u.created_at).toLocaleDateString()}</td>
                {canManage && (
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {u.role === "user" ? (
                      <div className="inline-flex flex-wrap items-center justify-end gap-2">
                        <ActionButton
                          icon={PlusCircle}
                          label="Wallet +/-"
                          onClick={(e) => {
                            e.stopPropagation();
                            onAdjust(u, "credit", "wallet");
                          }}
                          testId={`adjust-wallet-${u.id}`}
                          tone="emerald"
                        />
                        <ActionButton
                          icon={Gift}
                          label="Free +/-"
                          onClick={(e) => {
                            e.stopPropagation();
                            onAdjust(u, "credit", "free_play");
                          }}
                          testId={`adjust-free-play-${u.id}`}
                          tone="cyan"
                        />
                        <ActionButton
                          icon={KeyRound}
                          label="Reset pass"
                          onClick={(e) => {
                            e.stopPropagation();
                            onReset(u);
                          }}
                          testId={`reset-user-password-${u.id}`}
                          tone="sky"
                        />
                        <ActionButton
                          icon={Trash2}
                          label="Delete acc"
                          onClick={(e) => {
                            e.stopPropagation();
                            onDelete(u);
                          }}
                          testId={`delete-user-${u.id}`}
                          tone="red"
                        />
                      </div>
                    ) : (
                      <span className="text-xs text-slate-600">Locked</span>
                    )}
                  </td>
                )}
              </tr>
            ))}
            {users.length === 0 && <tr><td colSpan={canManage ? 8 : 7} className="py-10 text-center text-slate-500">No users yet.</td></tr>}
            {users.length > 0 && filteredUsers.length === 0 && (
              <tr>
                <td colSpan={canManage ? 8 : 7} className="py-10 text-center text-slate-500">
                  No {filterLabel ? `${filterLabel} ` : ""}users found{search.trim() ? ` for "${search.trim()}".` : "."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="mt-4 flex flex-col gap-3 text-sm text-slate-400 sm:flex-row sm:items-center sm:justify-between">
        <span>
          Showing {pageStart}-{pageEnd} of {total} accounts
        </span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => onPage(Math.max(1, page - 1))}
            disabled={page <= 1}
            className="rounded-lg border border-white/10 px-3 py-2 font-semibold text-slate-200 hover:bg-white/5 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Previous
          </button>
          <span className="min-w-24 text-center text-xs font-semibold uppercase tracking-wider text-slate-500">
            Page {page} / {totalPages}
          </span>
          <button
            type="button"
            onClick={() => onPage(Math.min(totalPages, page + 1))}
            disabled={page >= totalPages}
            className="rounded-lg border border-white/10 px-3 py-2 font-semibold text-slate-200 hover:bg-white/5 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Next
          </button>
        </div>
      </div>
    </>
  );
}

function UserDetailView({
  activeTab,
  canManage,
  detail,
  detailLoading,
  onAddAccount,
  onBack,
  onDelete,
  onEditAccount,
  onIdentityDocuments,
  onIdentityReview,
  onDiditRefresh,
  onManualVerify,
  onRefresh,
  onReset,
  saving,
  onTab,
  onWalletAdjust,
  user,
}) {
  const transactions = detail?.transactions || [];
  const accounts = detail?.game_accounts || [];
  const referrals = detail?.referrals || { count: 0, referred: [], rewards: [], reward_total: 0 };
  const stats = detail?.stats || {};

  if (!user && detailLoading) {
    return <div className="rounded-lg border border-white/10 bg-[#121214] p-8 text-sm text-slate-400">Loading user...</div>;
  }

  if (!user) {
    return (
      <div className="rounded-lg border border-white/10 bg-[#121214] p-8">
        <button onClick={onBack} className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-white">
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-start justify-between gap-4">
        <div className="flex min-w-0 items-start gap-4">
          <button
            type="button"
            onClick={onBack}
            className="mt-1 inline-flex items-center gap-2 rounded-md px-2 py-1 text-sm text-slate-300 hover:bg-white/5 hover:text-white"
          >
            <ArrowLeft className="h-4 w-4" /> Back
          </button>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="truncate text-2xl font-semibold tracking-tight">
                <VerifiedName name={user.username || "User"} user={user} />
              </h1>
              <StatusPill status={user.role === "admin" ? "admin" : "active"} />
            </div>
            <p className="mt-1 truncate text-sm text-slate-500">{user.email}</p>
          </div>
        </div>

        <div className="text-right">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Room Balance</p>
          <p className="text-2xl font-black tabular-nums text-[#2F6FA5]">{formatMoney(user.coins || 0)}</p>
          {detailLoading && <p className="text-xs text-slate-500">Refreshing...</p>}
        </div>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2 border-b border-white/10">
        {DETAIL_TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => onTab(tab.key)}
            className={`border-b-2 px-4 py-3 text-sm font-semibold transition ${
              activeTab === tab.key
                ? "border-[#2F6FA5] text-[#2F6FA5]"
                : "border-transparent text-slate-400 hover:text-white"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "overview" && (
        <OverviewTab
          accounts={accounts}
          referrals={referrals}
          stats={stats}
          transactions={transactions}
          user={user}
        />
      )}
      {activeTab === "wallet" && (
        <WalletTab
          canManage={canManage}
          onWalletAdjust={onWalletAdjust}
          transactions={transactions.filter(isWalletTransaction)}
          user={user}
        />
      )}
      {activeTab === "profile" && (
        <ProfileTab
          canManage={canManage}
          onIdentityDocuments={onIdentityDocuments}
          onIdentityReview={onIdentityReview}
          onDiditRefresh={onDiditRefresh}
          onManualVerify={onManualVerify}
          saving={saving}
          user={user}
        />
      )}
      {activeTab === "game_accounts" && (
        <GameAccountsTab
          accounts={accounts}
          canManage={canManage}
          onAdd={onAddAccount}
          onEdit={onEditAccount}
          onRefresh={onRefresh}
        />
      )}
      {activeTab === "transactions" && <TransactionsTab rows={transactions} />}
      {activeTab === "referrals" && <ReferralsTab referrals={referrals} user={user} />}

    </div>
  );
}

function OverviewTab({ accounts, referrals, stats, transactions, user }) {
  const approvedLoads = transactions.filter((row) => row.type === "load" && row.status === "approved");
  return (
    <div className="grid gap-4 lg:grid-cols-[1fr_280px]">
      <div className="rounded-lg border border-[#332a10] bg-[#101010] p-4">
        <InfoLine label="Username" value={user.username} />
        <InfoLine label="First name" value={user.first_name || "-"} />
        <InfoLine label="Middle name" value={user.middle_name || "-"} />
        <InfoLine label="Last name" value={user.last_name || "-"} />
        <InfoLine label="Full name" value={user.full_name || "-"} />
        <InfoLine label="Date of birth" value={user.date_of_birth || "-"} />
        <InfoLine label="Email" value={user.email} />
        <InfoLine label="Phone" value={user.phone || "-"} />
        <InfoLine
          label="First-load bonus used"
          value={stats.first_load_bonus_used ? "yes" : "no (first-time bonus still available)"}
        />
        <InfoLine label="Joined" value={formatDateTime(user.created_at)} />
        <InfoLine label="Game-account issues" value={stats.game_account_issues || 0} highlight />
        <InfoLine label="Referral rewards earned" value={`${formatMoney(referrals.reward_total || 0)} (${referrals.rewards?.length || 0})`} highlight />
      </div>
      <div className="grid gap-3">
        <MiniStat icon={WalletCards} label="Wallet" value={formatMoney(user.coins || 0)} />
        <MiniStat icon={Gift} label="Free play" value={formatMoney(user.free_play || 0)} />
        <MiniStat icon={Gamepad2} label="Game accounts" value={String(accounts.length)} />
        <MiniStat icon={ReceiptText} label="Approved loads" value={String(approvedLoads.length)} />
      </div>
    </div>
  );
}

function WalletTab({ canManage, onWalletAdjust, transactions, user }) {
  return (
    <div className="space-y-4">
      <div className="grid gap-3 md:grid-cols-2">
        <MiniStat icon={WalletCards} label="Wallet balance" value={formatMoney(user.coins || 0)} />
        <MiniStat icon={Gift} label="Free play" value={formatMoney(user.free_play || 0)} />
      </div>
      {canManage && (
        <div className="flex flex-wrap gap-2">
          <ActionButton icon={PlusCircle} label="Wallet +/-" onClick={() => onWalletAdjust(user, "credit", "wallet")} tone="emerald" />
          <ActionButton icon={Gift} label="Free +/-" onClick={() => onWalletAdjust(user, "credit", "free_play")} tone="cyan" />
        </div>
      )}
      <TransactionsTable rows={transactions} empty="No wallet transactions found." />
    </div>
  );
}

function ProfileTab({ canManage, onDiditRefresh, onIdentityDocuments, onIdentityReview, onManualVerify, saving, user }) {
  const phoneVerified = Boolean(user.phone_verified);
  const [phoneValue, setPhoneValue] = useState(user.phone || "");
  const [rejectNote, setRejectNote] = useState(user.identity_rejection_reason || "");
  const [manualDocs, setManualDocs] = useState({ front: null, back: null, selfie: null });
  const [manualDocsNote, setManualDocsNote] = useState("");

  useEffect(() => {
    setPhoneValue(user.phone || "");
    setRejectNote(user.identity_rejection_reason || "");
    setManualDocs({ front: null, back: null, selfie: null });
    setManualDocsNote("");
  }, [user.id, user.phone, user.identity_rejection_reason]);

  const phoneChanged = phoneValue.trim() !== (user.phone || "");
  const canSavePhone = canManage && phoneValue.trim() && phoneChanged;
  const canVerifyPhone = canManage && phoneValue.trim() && (!phoneVerified || phoneChanged);
  const identityStatus = user.identity_verification_status || "not_submitted";
  const identityApproved = identityStatus === "approved";
  const hasIdentityImages = Boolean(user.identity_id_image_url && user.identity_id_back_image_url && user.identity_selfie_image_url);
  const selectedDocCount = Number(Boolean(manualDocs.front)) + Number(Boolean(manualDocs.back)) + Number(Boolean(manualDocs.selfie));
  const hasAllIdentityImagesAfterUpload = Boolean(
    (manualDocs.front || user.identity_id_image_url)
      && (manualDocs.back || user.identity_id_back_image_url)
      && (manualDocs.selfie || user.identity_selfie_image_url)
  );

  const submitManualDocs = async (approve = false) => {
    await onIdentityDocuments({
      documents: manualDocs,
      approve,
      note: manualDocsNote.trim() || (approve ? "Admin uploaded manual KYC photos and approved" : "Admin uploaded manual KYC photos"),
    });
    setManualDocs({ front: null, back: null, selfie: null });
    setManualDocsNote("");
  };

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-[#332a10] bg-[#101010] p-4">
        <InfoLine label="User ID" value={user.id} />
        <InfoLine label="Username" value={user.username} />
        <InfoLine label="First name" value={user.first_name || "-"} />
        <InfoLine label="Middle name" value={user.middle_name || "-"} />
        <InfoLine label="Last name" value={user.last_name || "-"} />
        <InfoLine label="Full name" value={user.full_name || "-"} />
        <InfoLine label="Email" value={user.email} />
        <InfoLine label="Phone" value={user.phone || "-"} />
        <InfoLine label="Date of birth" value={user.date_of_birth || "-"} />
        <InfoLine label="State" value={user.state || "-"} />
        <InfoLine label="Phone verified" value={phoneVerified ? "yes" : "no"} />
        <InfoLine label="ID/selfie" value={identityStatus.replace(/_/g, " ")} />
        <InfoLine label="Referral code" value={user.referral_code || "-"} />
        <InfoLine label="Referred by" value={user.referred_by || "-"} />
        <InfoLine label="Created" value={formatDateTime(user.created_at)} />
      </div>

      {canManage && (
        <div className="rounded-lg border border-sky-500/20 bg-sky-500/10 p-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-base font-semibold text-white">Phone verification</h2>
              <p className="mt-1 max-w-2xl text-sm text-slate-400">
                Add or correct the customer's phone number here. Gmail verification is not required; marking the phone verified unlocks the account and adds the one-time $5 Freeplay when eligible.
              </p>
            </div>
            <StatusPill status={phoneVerified && !phoneChanged ? "approved" : "pending"} />
          </div>
          <label className="mt-4 block text-xs font-bold uppercase tracking-[0.22em] text-[#8FB7E1]">
            Phone number
          </label>
          <input
            value={phoneValue}
            onChange={(event) => setPhoneValue(event.target.value)}
            inputMode="tel"
            placeholder="+1 555 123 4567"
            data-testid={`manual-phone-input-${user.id}`}
            className="mt-2 w-full rounded-md border border-white/10 bg-[#0A0A0A] px-3 py-2.5 text-sm font-semibold text-white outline-none transition focus:border-sky-400"
          />
          {!phoneValue.trim() && (
            <div className="mt-3 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-200">
              Enter the customer's phone number before marking it verified.
            </div>
          )}
          <div className="mt-4 flex flex-wrap gap-2">
            <ActionButton
              disabled={saving || !canSavePhone}
              icon={Save}
              label="Save phone"
              onClick={() => onManualVerify({ phone: phoneValue, verifyPhone: false })}
              testId={`manual-phone-save-${user.id}`}
              tone="sky"
            />
            <ActionButton
              disabled={saving || !canVerifyPhone}
              icon={ShieldCheck}
              label={phoneVerified && !phoneChanged ? "Phone verified" : "Save & mark verified"}
              onClick={() => onManualVerify({ phone: phoneValue, verifyPhone: true })}
              testId={`manual-phone-verify-${user.id}`}
              tone="emerald"
            />
          </div>
        </div>
      )}

      <div className="rounded-lg border border-[#2F6FA5]/20 bg-[#07111d] p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="flex items-center gap-2 text-base font-semibold text-white">
              <IdCard className="h-4 w-4 text-[#8FB7E1]" />
              ID & selfie verification
            </h2>
            <p className="mt-1 max-w-2xl text-sm text-slate-400">
              Customer must be approved here before wallet withdrawal. Loading, playing, rewards, and game-account actions stay available.
            </p>
          </div>
          <StatusPill status={identityApproved ? "approved" : identityStatus === "rejected" ? "rejected" : "pending"} />
        </div>

        <div className="mt-4 rounded-lg border border-[#2F6FA5]/20 bg-[#06101b] p-3 text-sm text-slate-300">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.18em] text-[#8FB7E1]">Didit API</div>
              <div className="mt-1 text-xs text-slate-400">
                {user.didit_session_id
                  ? `Session: ${user.didit_session_id}`
                  : "No Didit session started yet. Customer can start Didit from their dashboard."}
              </div>
              {user.didit_last_checked_at && (
                <div className="mt-1 text-xs text-slate-500">Last checked: {formatDateTime(user.didit_last_checked_at)}</div>
              )}
            </div>
            {canManage && (
              <div className="flex flex-wrap gap-2">
                {user.didit_session_url && (
                  <a
                    href={user.didit_session_url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center justify-center rounded-md border border-white/10 bg-white/5 px-3 py-2 text-xs font-bold text-slate-100 hover:bg-white/10"
                  >
                    Open Didit
                  </a>
                )}
                <ActionButton
                  disabled={saving || !user.didit_session_id}
                  icon={ShieldCheck}
                  label="Refresh Didit API"
                  onClick={onDiditRefresh}
                  tone="sky"
                />
              </div>
            )}
          </div>
        </div>

        <div className="mt-4 grid gap-3 sm:grid-cols-3">
          <IdentityPreview label="ID front" name={user.identity_id_image_name} src={user.identity_id_image_url} />
          <IdentityPreview label="ID back" name={user.identity_id_back_image_name} src={user.identity_id_back_image_url} />
          <IdentityPreview label="Selfie" name={user.identity_selfie_image_name} src={user.identity_selfie_image_url} />
        </div>

        {identityStatus === "rejected" && user.identity_rejection_reason && (
          <div className="mt-3 rounded-md border border-rose-500/25 bg-rose-500/10 px-3 py-2 text-sm text-rose-100">
            {user.identity_rejection_reason}
          </div>
        )}

        {identityStatus === "pending" && user.identity_pending_reason && (
          <div className="mt-3 rounded-md border border-amber-500/25 bg-amber-500/10 px-3 py-2 text-sm text-amber-100">
            <div className="mb-1 text-xs font-bold uppercase tracking-[0.14em] text-amber-300">Why pending</div>
            {formatKycPendingReason(user.identity_pending_reason)}
          </div>
        )}

        {canManage && (
          <div className="mt-4 space-y-3">
            <div className="rounded-lg border border-white/10 bg-black/20 p-3">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h3 className="text-sm font-semibold text-white">Manual KYC photo upload</h3>
                  <p className="mt-1 text-xs text-slate-400">
                    Use this when a customer sends ID/selfie manually. Upload any missing photo, then approve if all three are present.
                  </p>
                </div>
                <span className="rounded-full border border-white/10 bg-white/5 px-2 py-1 text-[11px] font-bold text-slate-300">
                  {selectedDocCount} selected
                </span>
              </div>
              <div className="mt-3 grid gap-3 sm:grid-cols-3">
                <ManualKycFileInput
                  label="Upload ID front"
                  file={manualDocs.front}
                  onChange={(file) => setManualDocs((current) => ({ ...current, front: file }))}
                />
                <ManualKycFileInput
                  label="Upload ID back"
                  file={manualDocs.back}
                  onChange={(file) => setManualDocs((current) => ({ ...current, back: file }))}
                />
                <ManualKycFileInput
                  label="Upload selfie"
                  file={manualDocs.selfie}
                  onChange={(file) => setManualDocs((current) => ({ ...current, selfie: file }))}
                />
              </div>
              <input
                value={manualDocsNote}
                onChange={(event) => setManualDocsNote(event.target.value)}
                placeholder="Optional manual KYC note"
                className="mt-3 w-full rounded-md border border-white/10 bg-[#0A0A0A] px-3 py-2 text-sm text-white outline-none transition focus:border-sky-400"
              />
              <div className="mt-3 flex flex-wrap gap-2">
                <ActionButton
                  disabled={saving || selectedDocCount === 0}
                  icon={Save}
                  label="Save photos"
                  onClick={() => submitManualDocs(false)}
                  tone="sky"
                />
                <ActionButton
                  disabled={saving || !hasAllIdentityImagesAfterUpload || identityApproved}
                  icon={ShieldCheck}
                  label={identityApproved ? "Approved" : "Save photos & approve"}
                  onClick={() => submitManualDocs(true)}
                  tone="emerald"
                />
              </div>
            </div>

            <textarea
              value={rejectNote}
              onChange={(event) => setRejectNote(event.target.value)}
              placeholder="Reason if rejecting"
              className="min-h-20 w-full rounded-md border border-white/10 bg-[#0A0A0A] px-3 py-2 text-sm text-white outline-none transition focus:border-sky-400"
            />
            <div className="flex flex-wrap gap-2">
              <ActionButton
                disabled={saving || !hasIdentityImages || identityApproved}
                icon={ShieldCheck}
                label={identityApproved ? "Approved" : "Approve ID"}
                onClick={() => onIdentityReview({ status: "approved", note: "ID front, ID back, and selfie approved" })}
                tone="emerald"
              />
              <ActionButton
                disabled={saving || !hasIdentityImages || !rejectNote.trim()}
                icon={X}
                label="Reject"
                onClick={() => onIdentityReview({ status: "rejected", note: rejectNote.trim() })}
                tone="red"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function IdentityPreview({ label, name, src }) {
  return (
    <div className="rounded-lg border border-white/10 bg-black/20 p-3">
      <div className="mb-2 text-xs font-bold uppercase tracking-wider text-slate-500">{label}</div>
      {src ? (
        <a href={src} target="_blank" rel="noreferrer" className="block">
          <img src={src} alt={name || label} className="h-44 w-full rounded-md bg-black object-contain" />
          <div className="mt-2 truncate text-xs text-[#8FB7E1]">{name || "Open full image"}</div>
        </a>
      ) : (
        <div className="grid h-44 place-items-center rounded-md border border-dashed border-white/10 text-sm text-slate-500">
          Not submitted
        </div>
      )}
    </div>
  );
}

function ManualKycFileInput({ file, label, onChange }) {
  const inputId = `manual-kyc-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
  return (
    <label htmlFor={inputId} className="block rounded-md border border-dashed border-white/15 bg-[#080c12] p-3 text-xs text-slate-300 transition hover:border-[#2F6FA5]/60">
      <span className="block font-bold uppercase tracking-[0.16em] text-[#8FB7E1]">{label}</span>
      <span className="mt-2 block truncate text-slate-400">{file?.name || "Choose image"}</span>
      <input
        id={inputId}
        type="file"
        accept="image/*"
        className="sr-only"
        onChange={(event) => onChange(event.target.files?.[0] || null)}
      />
    </label>
  );
}

function GameAccountsTab({ accounts, canManage, onAdd, onEdit, onRefresh }) {
  const copyAllAccounts = () => {
    const lines = accounts
      .filter((account) => account.username || account.password)
      .map((account) => [
        `${account.display_name || account.gameName || account.gameId || "Game"}`,
        `Username: ${account.username || "-"}`,
        `Password: ${account.password || "-"}`,
      ].join("\n"));
    copyToClipboard(lines.join("\n\n"), "All game login details copied");
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Game Accounts</h2>
          <p className="text-sm text-slate-500">{accounts.length} saved credentials</p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={copyAllAccounts}
            disabled={!accounts.length}
            className="inline-flex items-center gap-2 rounded-md border border-[#2F6FA5]/40 px-3 py-2 text-sm font-semibold text-[#8FB7E1] hover:bg-[#2F6FA5]/10 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Copy className="h-4 w-4" /> Copy all IDs/pass
          </button>
          <button
            type="button"
            onClick={onRefresh}
            className="rounded-md border border-white/10 px-3 py-2 text-sm font-semibold text-slate-300 hover:bg-white/5"
          >
            Refresh
          </button>
          {canManage && (
            <button
              type="button"
              onClick={onAdd}
              className="inline-flex items-center gap-2 rounded-md bg-[#2F6FA5] px-3 py-2 text-sm font-bold text-white hover:bg-[#4E91C8]"
            >
              <PlusCircle className="h-4 w-4" /> Add account
            </button>
          )}
        </div>
      </div>

      <div className="overflow-x-auto rounded-lg border border-[#332a10] bg-[#101010]">
        <table className="w-full min-w-[760px] text-sm">
          <thead className="bg-white/5 text-left text-xs uppercase tracking-wider text-slate-400">
            <tr>
              <th className="px-4 py-3">Game</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Username</th>
              <th className="px-4 py-3">Password</th>
              <th className="px-4 py-3">Updated</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {accounts.map((account) => (
              <tr key={account.id || `${account.gameName}-${account.username}`} className="border-t border-[#332a10]/70">
                <td className="px-4 py-3">
                  <div className="font-semibold text-white">{account.display_name || account.gameName || "-"}</div>
                  <div className="text-xs text-slate-500">{account.gameName || account.gameId || "-"}</div>
                </td>
                <td className="px-4 py-3"><StatusPill status="ready" /></td>
                <td className="px-4 py-3 font-medium text-white">
                  <CredentialWithCopy value={account.username} label="username" />
                </td>
                <td className="px-4 py-3 font-medium text-slate-300">
                  <CredentialWithCopy value={account.password} label="password" />
                </td>
                <td className="px-4 py-3 text-xs text-slate-500">{formatDateTime(account.updated_at || account.created_at)}</td>
                <td className="px-4 py-3 text-right">
                  {canManage ? (
                    <button
                      type="button"
                      onClick={() => onEdit(account)}
                      className="inline-flex items-center gap-2 rounded-md border border-white/10 px-3 py-2 text-xs font-semibold text-white hover:bg-white/5"
                    >
                      <Edit3 className="h-3.5 w-3.5" /> Update
                    </button>
                  ) : (
                    <span className="text-xs text-slate-600">View only</span>
                  )}
                </td>
              </tr>
            ))}
            {accounts.length === 0 && (
              <tr>
                <td colSpan={6} className="py-10 text-center text-slate-500">No game accounts saved yet.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CredentialWithCopy({ value, label }) {
  const text = String(value || "").trim();
  if (!text) return <span>-</span>;
  return (
    <span className="inline-flex max-w-[220px] items-center gap-2">
      <span className="truncate font-mono">{text}</span>
      <button
        type="button"
        onClick={() => copyToClipboard(text, `${label} copied`)}
        className="grid h-7 w-7 shrink-0 place-items-center rounded-md border border-white/10 text-slate-400 hover:bg-white/5 hover:text-white"
        title={`Copy ${label}`}
        aria-label={`Copy ${label}`}
      >
        <Copy className="h-3.5 w-3.5" />
      </button>
    </span>
  );
}

function TransactionsTab({ rows }) {
  return <TransactionsTable rows={rows} empty="No transactions found." />;
}

function ReferralsTab({ referrals, user }) {
  const referred = referrals.referred || [];
  return (
    <div className="space-y-4">
      <div className="grid gap-3 md:grid-cols-3">
        <MiniStat icon={Users} label="Referral code" value={user.referral_code || "-"} />
        <MiniStat icon={Users} label="Referrals" value={String(referrals.count || 0)} />
        <MiniStat icon={Gift} label="Rewards" value={formatMoney(referrals.reward_total || 0)} />
      </div>
      <div className="overflow-x-auto rounded-lg border border-[#332a10] bg-[#101010]">
        <table className="w-full min-w-[640px] text-sm">
          <thead className="bg-white/5 text-left text-xs uppercase tracking-wider text-slate-400">
            <tr>
              <th className="px-4 py-3">Username</th>
              <th className="px-4 py-3">Email</th>
              <th className="px-4 py-3">Joined</th>
              <th className="px-4 py-3 text-right">Wallet</th>
            </tr>
          </thead>
          <tbody>
            {referred.map((row) => (
              <tr key={row.id} className="border-t border-[#332a10]/70">
                <td className="px-4 py-3 font-semibold text-white">{row.username || "-"}</td>
                <td className="px-4 py-3 text-slate-400">{row.email || "-"}</td>
                <td className="px-4 py-3 text-xs text-slate-500">{formatDateTime(row.created_at)}</td>
                <td className="px-4 py-3 text-right font-semibold tabular-nums text-[#2F6FA5]">{formatMoney(row.coins || 0)}</td>
              </tr>
            ))}
            {referred.length === 0 && (
              <tr>
                <td colSpan={4} className="py-10 text-center text-slate-500">No referred users yet.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function transactionNote(row) {
  if (row?.transfer_kind === "game_redeem" && (row?.freeplay_redeem_rule || row?.phone_verification_freeplay_rule || row?.leaderboard_reward_freeplay_rule)) {
    const provider = Number(row.provider_amount || 0);
    const feePercent = Number(row.redeem_fee_percent || 80);
    const fee = Number(row.redeem_fee_amount_units || 0) || Math.round(provider * MONEY_UNIT * (feePercent / 100));
    const wallet = Number(row.wallet_credit_amount || row.amount || 0);
    return `Promo freeplay/referral rule: provider redeem ${formatMoney(provider * MONEY_UNIT)}, ${feePercent}% fee ${formatMoney(fee)}, wallet credit ${formatMoney(wallet)}.`;
  }
  return row?.note || row?.admin_note || "-";
}

function TransactionAmount({ row }) {
  if (row?.transfer_kind === "inactivity_expiry") return `-${formatMoney(row.amount)}`;
  if (row?.transfer_kind === "game_redeem" && (row?.freeplay_redeem_rule || row?.phone_verification_freeplay_rule || row?.leaderboard_reward_freeplay_rule)) {
    const provider = Number(row.provider_amount || 0) * MONEY_UNIT;
    const feePercent = Number(row.redeem_fee_percent || 80);
    const wallet = Number(row.wallet_credit_amount || row.amount || 0);
    return (
      <div className="text-right">
        <div>{formatMoney(wallet)} net</div>
        <div className="mt-0.5 text-[11px] font-medium text-slate-500">
          Provider {formatMoney(provider)} - {feePercent}% fee
        </div>
      </div>
    );
  }
  return Number(row?.amount || 0) ? formatMoney(row.amount) : "-";
}

function TransactionsTable({ rows, empty }) {
  const displayRows = rows.filter((row) => !HIDDEN_TRANSACTION_TYPES.has(String(row?.type || "").toLowerCase()));

  return (
    <div className="overflow-x-auto rounded-lg border border-[#332a10] bg-[#101010]">
      <table className="w-full min-w-[860px] text-sm">
        <thead className="bg-white/5 text-left text-xs uppercase tracking-wider text-slate-400">
          <tr>
            <th className="px-4 py-3">When</th>
            <th className="px-4 py-3">Type</th>
            <th className="px-4 py-3">Game / Method</th>
            <th className="px-4 py-3">Status</th>
            <th className="px-4 py-3 text-right">Amount</th>
            <th className="px-4 py-3">Note</th>
          </tr>
        </thead>
        <tbody>
          {displayRows.map((row, index) => (
            <tr key={row.id || `${row.created_at}-${index}`} className="border-t border-[#332a10]/70">
              <td className="px-4 py-3 text-slate-400">{formatDateTime(row.created_at || row.processed_at)}</td>
              <td className="px-4 py-3 font-medium text-white">{typeLabel(row.type)}</td>
              <td className="px-4 py-3 text-slate-300">{row.game_id || row.payment_platform || row.payment_provider || "-"}</td>
              <td className="px-4 py-3"><StatusPill status={row.status || "pending"} /></td>
              <td className="px-4 py-3 text-right font-semibold tabular-nums text-[#2F6FA5]"><TransactionAmount row={row} /></td>
              <td className="max-w-[340px] px-4 py-3 text-slate-400" title={transactionNote(row)}>
                <span className="line-clamp-2">{transactionNote(row)}</span>
              </td>
            </tr>
          ))}
          {displayRows.length === 0 && (
            <tr>
              <td colSpan={6} className="py-10 text-center text-slate-500">{empty}</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function GameAccountDialog({ accountEditing, form, games, onChange, onClose, onSubmit, saving }) {
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/70 p-4" onClick={() => !saving && onClose()}>
      <form onSubmit={onSubmit} onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg rounded-xl border border-[#5b4a17] bg-[#101010] p-6 shadow-2xl">
        <div className="mb-5 flex items-start justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold">{accountEditing ? "Update credentials" : "Add game account"}</h3>
            <p className="mt-1 text-sm text-slate-500">{accountEditing?.display_name || accountEditing?.gameName || "Manual credentials"}</p>
          </div>
          <button type="button" disabled={saving} onClick={onClose} className="rounded-md p-1 hover:bg-white/10 disabled:opacity-50">
            <X className="h-4 w-4" />
          </button>
        </div>

        <datalist id="admin-user-game-options">
          {games.map((game) => <option key={game.value} value={game.value}>{game.label}</option>)}
        </datalist>

        <label className="block text-sm">
          <span className="mb-1.5 block text-xs uppercase tracking-wider text-slate-500">Game</span>
          <input
            autoFocus
            list="admin-user-game-options"
            value={form.game_id}
            onChange={(e) => onChange({ game_id: e.target.value })}
            className="w-full rounded-md border border-white/10 bg-black/30 px-3 py-2 text-sm outline-none focus:border-[#2F6FA5]"
            placeholder="firekirin"
          />
        </label>

        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <label className="block text-sm">
            <span className="mb-1.5 block text-xs uppercase tracking-wider text-slate-500">Username</span>
            <input
              value={form.username}
              onChange={(e) => onChange({ username: e.target.value })}
              className="w-full rounded-md border border-white/10 bg-black/30 px-3 py-2 text-sm outline-none focus:border-[#2F6FA5]"
              placeholder="player123"
            />
          </label>
          <label className="block text-sm">
            <span className="mb-1.5 block text-xs uppercase tracking-wider text-slate-500">Password</span>
            <input
              value={form.password}
              onChange={(e) => onChange({ password: e.target.value })}
              className="w-full rounded-md border border-white/10 bg-black/30 px-3 py-2 text-sm outline-none focus:border-[#2F6FA5]"
              placeholder="password"
            />
          </label>
        </div>

        <label className="mt-4 block text-sm">
          <span className="mb-1.5 block text-xs uppercase tracking-wider text-slate-500">Provider user ID</span>
          <input
            value={form.provider_user_id}
            onChange={(e) => onChange({ provider_user_id: e.target.value })}
            className="w-full rounded-md border border-white/10 bg-black/30 px-3 py-2 text-sm outline-none focus:border-[#2F6FA5]"
            placeholder="optional"
          />
        </label>

        <label className="mt-4 block text-sm">
          <span className="mb-1.5 block text-xs uppercase tracking-wider text-slate-500">Note</span>
          <textarea
            value={form.note}
            onChange={(e) => onChange({ note: e.target.value })}
            rows={3}
            className="w-full rounded-md border border-white/10 bg-black/30 px-3 py-2 text-sm outline-none focus:border-[#2F6FA5]"
            placeholder="old ID, correction, customer request..."
          />
        </label>

        <div className="mt-6 flex justify-end gap-2">
          <button type="button" disabled={saving} onClick={onClose}
            className="rounded-md px-4 py-2 text-sm hover:bg-white/5 disabled:opacity-50">
            Cancel
          </button>
          <button disabled={saving}
            className="inline-flex items-center gap-2 rounded-md bg-[#2F6FA5] px-4 py-2 text-sm font-bold text-white hover:bg-[#4E91C8] disabled:opacity-50">
            <Save className="h-4 w-4" /> {saving ? "Saving..." : "Save credentials"}
          </button>
        </div>
      </form>
    </div>
  );
}

function AdjustDialog({
  adjustKind,
  adjusting,
  amount,
  direction,
  note,
  onAmount,
  onClose,
  onDirection,
  onNote,
  onSubmit,
  saving,
}) {
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" onClick={() => !saving && onClose()}>
      <form onSubmit={onSubmit} onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-xl border border-white/10 bg-[#121214] p-6 shadow-2xl">
        <div className="mb-5 flex items-start justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold">{adjustKind === "free_play" ? (direction === "credit" ? "Add free play" : "Minus free play") : (direction === "credit" ? "Add wallet money" : "Minus wallet money")}</h3>
            <p className="mt-1 text-sm text-slate-500">
              {adjusting.username} - current {formatMoney(adjustKind === "free_play" ? adjusting.free_play || 0 : adjusting.coins || 0)} {adjustKind === "free_play" ? "free play" : "wallet"}
            </p>
          </div>
          <button type="button" disabled={saving} onClick={onClose} className="rounded-md p-1 hover:bg-white/10 disabled:opacity-50">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <button type="button" onClick={() => onDirection("credit")}
            className={`inline-flex items-center justify-center gap-2 rounded-md border px-3 py-2 text-sm font-semibold ${
              direction === "credit" ? "border-emerald-500/50 bg-emerald-500/15 text-emerald-300" : "border-white/10 text-slate-300 hover:bg-white/5"
            }`}>
            <PlusCircle className="h-4 w-4" /> Add
          </button>
          <button type="button" onClick={() => onDirection("debit")}
            className={`inline-flex items-center justify-center gap-2 rounded-md border px-3 py-2 text-sm font-semibold ${
              direction === "debit" ? "border-amber-500/50 bg-amber-500/15 text-amber-300" : "border-white/10 text-slate-300 hover:bg-white/5"
            }`}>
            <MinusCircle className="h-4 w-4" /> Minus
          </button>
        </div>

        <label className="mt-4 block text-sm">
          <span className="mb-1.5 block text-xs uppercase tracking-wider text-slate-500">Amount ($)</span>
          <input
            autoFocus
            type="number"
            min="0.01"
            step="0.01"
            value={amount}
            onChange={(e) => onAmount(e.target.value)}
            className="w-full rounded-md border border-white/10 bg-black/20 px-3 py-2 text-sm outline-none focus:border-[#2F6FA5]"
          />
        </label>

        <label className="mt-4 block text-sm">
          <span className="mb-1.5 block text-xs uppercase tracking-wider text-slate-500">Note</span>
          <textarea
            value={note}
            onChange={(e) => onNote(e.target.value)}
            rows={3}
            placeholder={adjustKind === "free_play" ? "Bonus free play, promotion, correction..." : "Cash received, bonus, correction..."}
            className="w-full rounded-md border border-white/10 bg-black/20 px-3 py-2 text-sm outline-none focus:border-[#2F6FA5]"
          />
        </label>

        <div className="mt-6 flex justify-end gap-2">
          <button type="button" disabled={saving} onClick={onClose}
            className="rounded-md px-4 py-2 text-sm hover:bg-white/5 disabled:opacity-50">
            Cancel
          </button>
          <button disabled={saving}
            className={`rounded-md px-4 py-2 text-sm font-bold text-black disabled:opacity-50 ${
              direction === "credit" ? "bg-emerald-300 hover:bg-emerald-200" : "bg-[#2F6FA5] hover:bg-[#4E91C8]"
            }`}>
            {saving ? "Saving..." : adjustKind === "free_play" ? (direction === "credit" ? "Add free play" : "Minus free play") : (direction === "credit" ? "Add dollars" : "Minus dollars")}
          </button>
        </div>
      </form>
    </div>
  );
}

function ResetPasswordDialog({
  newPassword,
  onClose,
  onGenerate,
  onNewPassword,
  onNote,
  onSubmit,
  resetNote,
  resetting,
  saving,
}) {
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" onClick={() => !saving && onClose()}>
      <form onSubmit={onSubmit} onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-xl border border-white/10 bg-[#121214] p-6 shadow-2xl">
        <div className="mb-5 flex items-start justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold">Reset user password</h3>
            <p className="mt-1 text-sm text-slate-500">{resetting.username} will use this password to log in.</p>
          </div>
          <button type="button" disabled={saving} onClick={onClose} className="rounded-md p-1 hover:bg-white/10 disabled:opacity-50">
            <X className="h-4 w-4" />
          </button>
        </div>

        <label className="block text-sm">
          <span className="mb-1.5 block text-xs uppercase tracking-wider text-slate-500">New password</span>
          <div className="flex gap-2">
            <input
              autoFocus
              type="text"
              minLength={6}
              value={newPassword}
              onChange={(e) => onNewPassword(e.target.value)}
              className="min-w-0 flex-1 rounded-md border border-white/10 bg-black/20 px-3 py-2 text-sm outline-none focus:border-[#2F6FA5]"
            />
            <button type="button" onClick={onGenerate}
              className="rounded-md border border-sky-500/30 bg-sky-500/10 px-3 py-2 text-xs font-semibold text-sky-200 hover:bg-sky-500/20">
              Generate
            </button>
          </div>
        </label>

        <label className="mt-4 block text-sm">
          <span className="mb-1.5 block text-xs uppercase tracking-wider text-slate-500">Note</span>
          <textarea
            value={resetNote}
            onChange={(e) => onNote(e.target.value)}
            rows={3}
            placeholder="Customer forgot password, support verified..."
            className="w-full rounded-md border border-white/10 bg-black/20 px-3 py-2 text-sm outline-none focus:border-[#2F6FA5]"
          />
        </label>

        <div className="mt-6 flex justify-end gap-2">
          <button type="button" disabled={saving} onClick={onClose}
            className="rounded-md px-4 py-2 text-sm hover:bg-white/5 disabled:opacity-50">
            Cancel
          </button>
          <button disabled={saving}
            className="rounded-md bg-sky-300 px-4 py-2 text-sm font-bold text-black hover:bg-sky-200 disabled:opacity-50">
            {saving ? "Saving..." : "Reset password"}
          </button>
        </div>
      </form>
    </div>
  );
}

function DeleteDialog({ deleting, onClose, onSubmit, saving }) {
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" onClick={() => !saving && onClose()}>
      <form onSubmit={onSubmit} onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-xl border border-red-500/20 bg-[#121214] p-6 shadow-2xl">
        <div className="mb-5 flex items-start justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold text-red-200">Delete account</h3>
            <p className="mt-1 text-sm text-slate-500">
              {deleting.username} will no longer be able to log in. Wallet history stays for admin audit.
            </p>
          </div>
          <button type="button" disabled={saving} onClick={onClose} className="rounded-md p-1 hover:bg-white/10 disabled:opacity-50">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="rounded-lg border border-red-500/20 bg-red-500/10 p-3 text-sm">
          <div className="font-medium text-red-100">{deleting.username}</div>
          <div className="text-red-100/70">{deleting.email}</div>
          <div className="mt-2 text-xs text-red-100/60">
            Current wallet {formatMoney(deleting.coins)} - free play {formatMoney(deleting.free_play || 0)}
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button type="button" disabled={saving} onClick={onClose}
            className="rounded-md px-4 py-2 text-sm hover:bg-white/5 disabled:opacity-50">
            Cancel
          </button>
          <button disabled={saving}
            className="rounded-md bg-red-400 px-4 py-2 text-sm font-bold text-black hover:bg-red-300 disabled:opacity-50">
            {saving ? "Deleting..." : "Delete account"}
          </button>
        </div>
      </form>
    </div>
  );
}

function ActionButton({ disabled = false, icon: Icon, label, onClick, testId, tone = "slate" }) {
  const tones = {
    cyan: "border-cyan-500/30 bg-cyan-500/10 text-cyan-200 hover:bg-cyan-500/20",
    emerald: "border-emerald-500/30 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20",
    red: "border-red-500/30 bg-red-500/10 text-red-200 hover:bg-red-500/20",
    sky: "border-sky-500/30 bg-sky-500/10 text-sky-200 hover:bg-sky-500/20",
    slate: "border-white/10 bg-white/5 text-slate-200 hover:bg-white/10",
  };
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      disabled={disabled}
      className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs font-semibold disabled:cursor-not-allowed disabled:opacity-50 ${tones[tone] || tones.slate}`}
    >
      <Icon className="h-3.5 w-3.5" /> {label}
    </button>
  );
}

function MiniStat({ icon: Icon, label, value }) {
  return (
    <div className="rounded-lg border border-[#332a10] bg-[#101010] p-4">
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
        <Icon className="h-4 w-4 text-[#2F6FA5]" /> {label}
      </div>
      <div className="mt-2 truncate text-xl font-black tabular-nums text-white">{value}</div>
    </div>
  );
}

function InfoLine({ highlight, label, value }) {
  return (
    <p className="mb-3 text-sm last:mb-0">
      <span className="font-semibold text-white">{label}: </span>
      <span className={highlight ? "font-semibold text-[#2F6FA5]" : "text-slate-200"}>{value || "-"}</span>
    </p>
  );
}

function StatusPill({ status }) {
  const label = String(status || "-").replaceAll("_", " ");
  return (
    <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold capitalize ${statusTone(status)}`}>
      {label}
    </span>
  );
}

function generatePassword() {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$";
  const values = new Uint32Array(10);
  window.crypto.getRandomValues(values);
  return Array.from(values, (value) => chars[value % chars.length]).join("");
}
